// Baby Monitor (Beta). One page; two modes driven by who currently
// holds the Hub claim:
//   - Nobody → CTA: "Become Hub" (requires mic permission).
//   - Someone else → CTA: "Listen" (joins as Monitor).
//   - You → live "Broadcasting" view with monitor count + Release Hub.
//
// Bare-bones P1: no talk-back yet. The Beta toggle in Settings controls
// whether the navigation link is visible.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useMe } from '../api/hooks.ts';
import { HeaderBar } from '../components/HeaderBar.tsx';
import {
  acquireWakeLock,
  claimHub,
  fetchSnapshot,
  type HubSession,
  type MonitorSession,
  type MonitorSnapshot,
  openPresenceSocket,
  playBeep,
  publishTrack,
  releaseHub,
  releaseWakeLock,
  startHub,
  startMonitor,
} from '../lib/monitor.ts';

function newPeerId() {
  return crypto.randomUUID();
}

export function Monitor() {
  const { data: me } = useMe();
  const [snapshot, setSnapshot] = useState<MonitorSnapshot | null>(null);
  const [hubSession, setHubSession] = useState<HubSession | null>(null);
  const [monitorSession, setMonitorSession] = useState<MonitorSession | null>(null);
  const [busy, setBusy] = useState<'starting-hub' | 'joining' | 'releasing' | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const peerId = useMemo(newPeerId, []);
  const deviceLabel = useMemo(() => {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return 'iPhone';
    if (/iPad/.test(ua)) return 'iPad';
    if (/Android/.test(ua)) return 'Android';
    if (/Mac/.test(ua)) return 'Mac';
    return 'Browser';
  }, []);

  // Initial state pull + presence socket.
  useEffect(() => {
    let cancelled = false;
    fetchSnapshot()
      .then((s) => {
        if (!cancelled) setSnapshot(s);
      })
      .catch(() => {
        /* unauth or no-family — render-time handled */
      });
    const cleanup = openPresenceSocket({
      role: hubSession ? 'hub' : 'monitor',
      peerId,
      deviceLabel,
      onSnapshot: (s) => setSnapshot(s),
    });
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [peerId, deviceLabel, hubSession]);

  // Attach monitor session's stream to the <audio> element. Also wire
  // MediaSession metadata so iOS / Android treat this as proper
  // background audio (otherwise Safari aggressively suspends the tab
  // when backgrounded or screen-locked, killing the stream).
  useEffect(() => {
    if (!audioRef.current || !monitorSession) return;
    audioRef.current.srcObject = monitorSession.stream;
    audioRef.current.play().catch(() => {
      toast.error('Tap "Listen" again to allow audio playback.');
    });
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: 'Baby Monitor',
        artist: snapshot?.hub?.deviceLabel ?? 'Hub',
        album: 'Lion Tracker',
      });
      navigator.mediaSession.playbackState = 'playing';
      // No-op handlers — declaring them is what makes iOS show lock-
      // screen controls and treat us as a proper background audio app.
      const noop = () => {};
      try {
        navigator.mediaSession.setActionHandler('play', noop);
        navigator.mediaSession.setActionHandler('pause', () => onLeaveMonitor());
      } catch {
        /* older browsers */
      }
    }
    return () => {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'none';
        navigator.mediaSession.metadata = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monitorSession]);

  // Tear down on unload.
  useEffect(() => {
    return () => {
      hubSession?.stop();
      monitorSession?.stop();
      releaseWakeLock().catch(() => {});
    };
  }, [hubSession, monitorSession]);

  const onBecomeHub = useCallback(async () => {
    if (busy) return;
    setBusy('starting-hub');
    try {
      const session = await startHub();
      try {
        await claimHub(deviceLabel, session.sessionId);
      } catch (e) {
        // Likely 409 — someone else grabbed it first. Drop our mic.
        session.stop();
        throw e;
      }
      await publishTrack(session.trackName);
      await acquireWakeLock();
      setHubSession(session);
      toast.success('Broadcasting');
    } catch (e) {
      const msg = String(e instanceof Error ? e.message : e);
      if (msg.includes('realtime_not_configured')) {
        toast.error('Realtime not configured on the server yet.');
      } else if (msg.includes('hub_taken')) {
        toast.error('Another device is already the Hub.');
      } else if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
        toast.error('Mic permission required.');
      } else {
        toast.error('Could not start Hub.');
      }
    } finally {
      setBusy(null);
    }
  }, [busy, deviceLabel]);

  const onReleaseHub = useCallback(async () => {
    if (busy) return;
    setBusy('releasing');
    try {
      // hubSession may be null (orphan case: we hold the server-side claim
      // but our local mic session was torn down by a navigation). Still
      // release the server-side claim either way.
      hubSession?.stop();
      await releaseHub();
      await releaseWakeLock();
      setHubSession(null);
    } catch {
      toast.error('Could not stop the broadcast — try again.');
    } finally {
      setBusy(null);
    }
  }, [busy, hubSession]);

  const onJoinAsMonitor = useCallback(async () => {
    if (busy || !snapshot?.hub?.publishedTrackName) return;
    setBusy('joining');
    try {
      const session = await startMonitor(
        snapshot.hub.callsSessionId,
        snapshot.hub.publishedTrackName,
      );
      setMonitorSession(session);
    } catch {
      toast.error('Could not join.');
    } finally {
      setBusy(null);
    }
  }, [busy, snapshot]);

  const onLeaveMonitor = useCallback(() => {
    // Pause + detach the audio element BEFORE tearing down the session.
    // Otherwise the element can keep playing buffered audio after we
    // close the PeerConnection, which made "Stop listening" appear
    // broken — the user had to force-quit the PWA to actually go quiet.
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.srcObject = null;
    }
    monitorSession?.stop();
    setMonitorSession(null);
  }, [monitorSession]);

  // "You" is the hub if the server says so, regardless of whether the in-memory
  // mic session is still alive. Coming back to this page after navigating
  // away drops the local hubSession but the server-side claim can survive
  // the 15s websocket-grace window.
  const serverSaysYouAreHub = !!snapshot?.hub && snapshot.hub.userId === me?.user.id;
  const youAreHub = serverSaysYouAreHub;
  const youAreHubButOrphaned = serverSaysYouAreHub && !hubSession;
  const someoneElseIsHub = !!snapshot?.hub && snapshot.hub.userId !== me?.user.id;
  const nobodyIsHub = !snapshot?.hub;

  return (
    <>
      <HeaderBar title="Baby Monitor" subtitle="Beta · audio only" />
      <div className="lt-scroll" style={{ flex: 1, padding: 16 }}>
        <p style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.45, margin: '0 0 16px' }}>
          One phone in the family acts as the <strong>Hub</strong> (place near
          the baby). Other phones <strong>Listen</strong>. The Hub needs to
          stay unlocked with this page open — background streaming isn’t
          supported on web apps yet.
        </p>

        {youAreHub && (
          <Card kind="active">
            <h3 style={{ margin: 0, fontSize: 18 }}>
              {youAreHubButOrphaned ? 'You hold the Hub claim' : 'Broadcasting'}
            </h3>
            <p style={{ margin: '6px 0 12px', fontSize: 13, color: 'var(--text-2)' }}>
              {youAreHubButOrphaned ? (
                <>
                  This device is registered as Hub but the mic stream isn’t
                  live here (probably from a tab close or navigation). Stop
                  to release the slot so any device can take over.
                </>
              ) : (
                <>
                  {snapshot?.monitors.length ?? 0} listener
                  {(snapshot?.monitors.length ?? 0) === 1 ? '' : 's'} ·{' '}
                  {snapshot && new Date(snapshot.hub?.startedAt ?? 0).toLocaleTimeString()}
                </>
              )}
            </p>
            {hubSession && <HubControls session={hubSession} />}
            <button
              type="button"
              onClick={onReleaseHub}
              disabled={busy === 'releasing'}
              style={btnDanger}
            >
              {busy === 'releasing' ? 'Stopping…' : 'Stop broadcasting'}
            </button>
          </Card>
        )}

        {nobodyIsHub && (
          <Card>
            <h3 style={{ margin: 0, fontSize: 18 }}>Become Hub</h3>
            <p style={{ margin: '6px 0 12px', fontSize: 13, color: 'var(--text-2)' }}>
              Captures audio from this device’s microphone and streams it to
              everyone else in the family.
            </p>
            <button
              type="button"
              onClick={onBecomeHub}
              disabled={busy === 'starting-hub'}
              style={btnPrimary}
            >
              {busy === 'starting-hub' ? 'Starting…' : 'Become Hub'}
            </button>
          </Card>
        )}

        {someoneElseIsHub && (
          <Card>
            <h3 style={{ margin: 0, fontSize: 18 }}>
              {snapshot?.hub?.deviceLabel ?? 'Hub'} is broadcasting
            </h3>
            <p style={{ margin: '6px 0 12px', fontSize: 13, color: 'var(--text-2)' }}>
              {monitorSession ? 'You are listening.' : 'Tap below to listen in.'}
            </p>
            {!monitorSession ? (
              <button
                type="button"
                onClick={onJoinAsMonitor}
                disabled={busy === 'joining' || !snapshot?.hub?.publishedTrackName}
                style={btnPrimary}
              >
                {busy === 'joining' ? 'Connecting…' : 'Listen'}
              </button>
            ) : (
              <>
                <ListenerPanel session={monitorSession} />
                <button type="button" onClick={onLeaveMonitor} style={btnSecondary}>
                  Stop listening
                </button>
              </>
            )}
          </Card>
        )}

        <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />

        {snapshot && snapshot.monitors.length > 0 && (
          <Card>
            <h4 style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.04 }}>
              Listening now
            </h4>
            {snapshot.monitors.map((m) => (
              <div key={m.peerId} style={{ fontSize: 14, padding: '4px 0', color: 'var(--text-1)' }}>
                {m.deviceLabel}
              </div>
            ))}
          </Card>
        )}
      </div>
    </>
  );
}

function HubControls({ session }: { session: HubSession }) {
  const [muted, setMutedState] = useState(false);
  const [level, setLevel] = useState(0);

  // requestAnimationFrame loop to sample the analyser ~60Hz for the
  // level meter. Cheap; stops on unmount. Will freeze when iOS
  // suspends the AudioContext on lock — that's expected; the SFU
  // stream uses the raw mic track and keeps flowing.
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      setLevel(session.readLevel());
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [session]);

  return (
    <div style={{ marginBottom: 12 }}>
      <LevelMeter value={muted ? 0 : level} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginTop: 10,
          fontSize: 13,
          color: 'var(--text-2)',
        }}
      >
        <span style={{ flex: 1, fontSize: 12, color: 'var(--text-3)' }}>
          Meter freezes when screen locks — broadcast keeps going.
        </span>
        <button
          type="button"
          onClick={() => {
            const next = !muted;
            setMutedState(next);
            session.setMuted(next);
          }}
          style={{
            padding: '8px 14px',
            borderRadius: 999,
            border: '1px solid var(--border)',
            background: muted ? 'var(--danger)' : 'var(--bg-sunken)',
            color: muted ? 'white' : 'var(--text-1)',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {muted ? 'Unmute' : 'Mute'}
        </button>
      </div>
    </div>
  );
}

function ListenerPanel({ session }: { session: MonitorSession }) {
  const [boost, setBoost] = useState(1);
  const [level, setLevel] = useState(0);
  // Connection state: 'new' | 'connecting' | 'connected' | 'disconnected'
  // | 'failed' | 'closed'. We treat anything that isn't 'connected'
  // (after first reaching it) as a disconnect → beep.
  const [conn, setConn] = useState<RTCPeerConnectionState>('new');
  const everConnected = useRef(false);

  // Alarm config (persisted in localStorage so it survives reloads).
  const [alarmOn, setAlarmOn] = useState(
    () => localStorage.getItem('lt-monitor-alarm-on') === '1',
  );
  const [threshold, setThreshold] = useState(() => {
    const v = Number(localStorage.getItem('lt-monitor-alarm-threshold'));
    return Number.isFinite(v) && v > 0 ? v : 0.55;
  });
  const [duration, setDuration] = useState(() => {
    const v = Number(localStorage.getItem('lt-monitor-alarm-duration'));
    return Number.isFinite(v) && v > 0 ? v : 3;
  });
  // Tick the alarm based on how long the level has been over threshold.
  const aboveSince = useRef<number | null>(null);
  const lastAlarmAt = useRef(0);

  // Connection-state watch + disconnect beep.
  useEffect(() => {
    return session.onConnectionState((state) => {
      setConn(state);
      if (state === 'connected') everConnected.current = true;
      else if (
        everConnected.current &&
        (state === 'disconnected' || state === 'failed' || state === 'closed')
      ) {
        playBeep('disconnect');
      }
    });
  }, [session]);

  // Level sampling + alarm logic.
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const l = session.readLevel();
      setLevel(l);
      if (alarmOn) {
        if (l >= threshold) {
          if (aboveSince.current == null) aboveSince.current = performance.now();
          const heldFor = (performance.now() - aboveSince.current) / 1000;
          // Cool-down: don't refire alarm more than once per 8s while sound
          // is still above threshold, otherwise we drown out the room.
          if (heldFor >= duration && performance.now() - lastAlarmAt.current > 8000) {
            playBeep('alert');
            lastAlarmAt.current = performance.now();
          }
        } else {
          aboveSince.current = null;
        }
      } else {
        aboveSince.current = null;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [session, alarmOn, threshold, duration]);

  const connBadge = (() => {
    if (conn === 'connected') return { c: 'var(--accent)', t: 'Connected' };
    if (conn === 'connecting' || conn === 'new')
      return { c: 'var(--text-3)', t: 'Connecting…' };
    return { c: 'var(--danger)', t: 'Disconnected' };
  })();

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span
          aria-hidden
          style={{ width: 8, height: 8, borderRadius: 999, background: connBadge.c }}
        />
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{connBadge.t}</span>
      </div>

      <LevelMeter value={level} threshold={alarmOn ? threshold : undefined} />

      <label
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          marginTop: 10,
          fontSize: 13,
          color: 'var(--text-2)',
        }}
      >
        <span>
          Boost{' '}
          <span className="lt-tabular" style={{ color: 'var(--text-3)' }}>
            {boost.toFixed(1)}×
          </span>
        </span>
        <input
          type="range"
          min={0.5}
          max={6}
          step={0.1}
          value={boost}
          onChange={(e) => {
            const g = Number(e.target.value);
            setBoost(g);
            session.setBoost(g);
          }}
        />
      </label>

      <div
        style={{
          marginTop: 12,
          padding: 12,
          borderRadius: 10,
          background: 'var(--bg-sunken)',
          border: '1px solid var(--border)',
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--text-1)',
            cursor: 'pointer',
          }}
        >
          <span>Sound alarm</span>
          <input
            type="checkbox"
            checked={alarmOn}
            onChange={(e) => {
              setAlarmOn(e.target.checked);
              localStorage.setItem('lt-monitor-alarm-on', e.target.checked ? '1' : '0');
            }}
            style={{ width: 18, height: 18 }}
          />
        </label>
        {alarmOn && (
          <>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                marginTop: 10,
                fontSize: 12,
                color: 'var(--text-2)',
              }}
            >
              <span>
                Threshold{' '}
                <span className="lt-tabular" style={{ color: 'var(--text-3)' }}>
                  {Math.round(threshold * 100)}%
                </span>
              </span>
              <input
                type="range"
                min={0.2}
                max={0.95}
                step={0.05}
                value={threshold}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setThreshold(v);
                  localStorage.setItem('lt-monitor-alarm-threshold', String(v));
                }}
              />
            </label>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                marginTop: 8,
                fontSize: 12,
                color: 'var(--text-2)',
              }}
            >
              <span>
                Sustained for{' '}
                <span className="lt-tabular" style={{ color: 'var(--text-3)' }}>
                  {duration}s
                </span>
              </span>
              <input
                type="range"
                min={1}
                max={15}
                step={1}
                value={duration}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setDuration(v);
                  localStorage.setItem('lt-monitor-alarm-duration', String(v));
                }}
              />
            </label>
            <button
              type="button"
              onClick={() => playBeep('alert')}
              style={{
                marginTop: 10,
                padding: '6px 12px',
                fontSize: 12,
                borderRadius: 999,
                background: 'transparent',
                border: '1px solid var(--border)',
                color: 'var(--text-2)',
              }}
            >
              Test alarm tone
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function LevelMeter({ value, threshold }: { value: number; threshold?: number }) {
  // value is 0..1 RMS-dB-normalised peak. 20 segments + optional
  // threshold tick so the user can see where the alarm will trigger
  // relative to current room sound.
  const segments = 20;
  const lit = Math.round(value * segments);
  const threshIdx = threshold != null ? Math.round(threshold * segments) : -1;
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 2, height: 14, alignItems: 'stretch' }}>
        {Array.from({ length: segments }).map((_, i) => {
          const active = i < lit;
          const colour =
            i < 12 ? 'var(--accent)' : i < 16 ? 'var(--warning, #d99000)' : 'var(--danger)';
          return (
            <div
              key={i}
              style={{
                flex: 1,
                borderRadius: 2,
                background: active ? colour : 'var(--bg-sunken)',
                opacity: active ? 1 : 0.5,
                transition: 'background 60ms linear',
              }}
            />
          );
        })}
      </div>
      {threshIdx >= 0 && threshIdx < segments && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -3,
            bottom: -3,
            // Sit between two segments at the threshold position.
            left: `calc(${(threshIdx / segments) * 100}% - 1px)`,
            width: 2,
            background: 'var(--text-1)',
            opacity: 0.6,
            borderRadius: 1,
          }}
        />
      )}
    </div>
  );
}

const cardBase: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 14,
  padding: 16,
  marginBottom: 12,
};

function Card({ children, kind }: { children: React.ReactNode; kind?: 'active' }) {
  return (
    <div
      style={{
        ...cardBase,
        ...(kind === 'active'
          ? { borderColor: 'var(--accent)', boxShadow: '0 0 0 3px color-mix(in srgb, var(--accent) 12%, transparent)' }
          : {}),
      }}
    >
      {children}
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 10,
  background: 'var(--accent)',
  color: 'white',
  border: 'none',
  fontSize: 15,
  fontWeight: 600,
};
const btnSecondary: React.CSSProperties = {
  ...btnPrimary,
  background: 'var(--bg-sunken)',
  color: 'var(--text-1)',
  border: '1px solid var(--border)',
};
const btnDanger: React.CSSProperties = {
  ...btnPrimary,
  background: 'var(--danger)',
};
